import { makeStyles, Typography, Grid, Box, Button } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Legend } from "chart.js";
import { Tooltip } from "@mui/material";
import { ICargoInsights } from "src/common/typings/insights";
import { getCargoAction } from "src/app-redux/insights/actions/insightsActions";
import { useAppDispatch } from "src/app-redux/hooks";
import CommonMaterialGrid from "src/components/CommonXGrid/CommonMaterialGrid";
import CommonDialog from "src/components/AlertDialog/CommonDialog";
import { GridRowModel } from "@material-ui/x-grid";
import { columns } from "./columns";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import { Check, Close } from "@material-ui/icons";
import moment from "moment";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Legend);

interface Props {
  startDate: string;
  endDate: string;
  consignmentTypeName: string;
}

const useStyles = makeStyles(() => ({
  vehiclesItem: {
    border: "0.5px solid #E6E6E6",
    borderRadius: "8px",
    boxShadow: "0px 0px 2px #00000029",
    padding: "15px",
  },
  heading: {
    fontSize: "16px",
    fontWeight: 700,
    marginBottom: "15px",
  },
  icons: {
    margin: "1px 10px 15px 0",
    height: "17px",
  },
  revenueAmount: {
    fontSize: "22px",
    fontWeight: 600,
    color: "#488CFB",
    marginBottom: "15px",
  },
}));

const aggregateData = (data: any) => {
  return data?.reduce((acc: any, curr: any) => {
    const cargoTypes = curr?.cargoType?.split(", ");
    cargoTypes?.forEach((type: any) => {
      if (!acc[type]) {
        acc[type] = { weight: 0, count: 0 };
      }
      if (curr.weight > 0) {
        acc[type].weight += curr.weight;
        acc[type].count += curr.count;
      }
    });
    return acc;
  }, {});
};

const createChartData = (data: any = []) => {
  const cargoTypes = Object.keys(data);
  const weights = cargoTypes.map((type) => data[type].weight);
  const counts = cargoTypes.map((type) => data[type].count);
  const totalWeight = weights.reduce((sum: any, weight: any) => sum + weight, 0);

  return {
    labels: cargoTypes,
    weights,
    counts,
    totalWeight,
  };
};

export function Cargo({ startDate, endDate, consignmentTypeName }: Props): React.ReactElement {
  const classes = useStyles();
  const [importList, setImportList] = useState<any>([]);
  const isExportString = localStorage.getItem("isAllowToExportData");
  const isExport = isExportString === "true";
  const importData: any = aggregateData(importList);
  const importChartData = createChartData(importData);
  const dispatch = useAppDispatch();
  const [cargoGridData, setCargoGridData] = React.useState<GridRowModel[]>([]);
  const [isOpendailog, setIsOpendailog] = useState(false);
  const [displayColumns, setDisplayColumns] = React.useState<any>(columns);
  const [loader, setLoader] = useState<any>(false);

  const getCargoInsight = async (startDate: string, endDate: string, consignmentTypeName: any) => {
    const req: ICargoInsights = {
      startDate: startDate,
      endDate: endDate,
      ConsignmentType: consignmentTypeName,
      isNodRequest: true,
    };
    const res = await dispatch(getCargoAction(req));

    if (res.payload) {
      setImportList(res.payload.cargoStatsModel);
    }
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        display: true,
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.dataset.label || "";
            const value = context.raw;
            const unit = label.includes("Weight") ? "Ton" : "";

            return `${label}: ${value} ${unit}`;
          },
          afterLabel: function (context: any) {
            const index = context.dataIndex;
            if (context.dataset.label.includes("Weight")) {
              return `Vehicle Count: ${importList[index]?.count}`;
            } else {
              return `Weight(Ton): ${importList[index]?.weight / 1000} Ton`;
            }
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        min: 0,
        grid: {
          color: "rgba(200, 200, 200, 0.3)",
        },
        ticks: {
          stepSize: 1,
          callback: function (value: any) {
            return `${value}`;
          },
        },
      },
    },
    onClick: async (event: any, chartElement: any) => {
      if (chartElement?.length > 0) {
        getCargoRec(chartElement[0]?.index);
      }
    },
  };

  const formatWeight = (weight: any) => {
    const weightInTons: any = (weight / 1_000).toLocaleString();
    return `${weightInTons} Ton${weightInTons > 1 ? "s" : ""}`;
  };

  const toggleDialogCloseHandler = async () => {
    setIsOpendailog(!isOpendailog);
  };
  const getCargoRec = async (index: number) => {
    const { cargoType } = importList[index];
    setLoader(true);
    setCargoGridData([]);
    setIsOpendailog(true);
    const results = await fetchWrapper.post("get-vehicle-details-by-cargo-type", {
      StartDate: startDate,
      EndDate: endDate,
      consignmentType: consignmentTypeName,
      CargoType: cargoType,
      isNodRequest: true,
    });
    setLoader(false);
    if (results) {
      setCargoGridData(results?.value?.gateEnteries);
    }
  };

  const exportHandler = async (row: any) => {
    dispatch(
      addNotificationAction({
        message: "Your download will begin shortly",
        options: { variant: "success" },
      })
    );
    const exportedRecords = await row?.map((data: any) => {
      return {
        Vehicle: data?.vehicleNumber,
        Driver: data?.driverName,
        Type: data?.vehicleType,
        Status: data?.vehicleStatus,
        Consignment: data?.consignmentType,
        "GD #": data?.gdNumber,
        Location: data?.terminalLocation,
        Cargo: data?.cargoTypeName,
        Yard: data?.yardName,
        Entry: data.gateEntryDate ? moment(data.gateEntryDate).format("DD-MM-YYYY hh:mm:ss A") : "",
        Checkout: data.gateOutDate ? moment(data.gateOutDate).format("DD-MM-YYYY hh:mm:ss A") : "",
        Anomaly: data.isAnomlyDetected ? "Yes" : "No",
        "In Progress": data.vehicleInProgress ? "Yes" : "No",
      };
    });
    return exportedRecords;
  };

  useEffect(() => {
    if (cargoGridData.length) {
      const transformedColumns: any = columns.map((column: any) =>
        column.accessorKey === "vehicleInProgress"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.vehicleInProgress ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.vehicleInProgress ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column.accessorKey === "isAnomlyDetected"
          ? {
              ...column,
              Cell: function customCell({ row }: any) {
                return (
                  <React.Fragment>
                    <Box>
                      <Tooltip title={row.original?.isAnomlyDetected ? "Yes" : "No"}>
                        <Button style={{ minWidth: "30px", padding: "4px", color: "gray" }}>
                          {row.original?.isAnomlyDetected ? <Check /> : <Close />}
                        </Button>
                      </Tooltip>
                    </Box>
                  </React.Fragment>
                );
              },
            }
          : column
      );

      setDisplayColumns(transformedColumns);
    }
  }, [cargoGridData]);

  useEffect(() => {
    getCargoInsight(startDate, endDate, consignmentTypeName);
  }, [startDate, endDate, consignmentTypeName]);
  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <div className={classes.vehiclesItem}>
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <Typography variant="body1" component="p" style={{ textAlign: "center" }}>
                {consignmentTypeName}
              </Typography>
              <Typography variant="h6" component="p">
                {formatWeight(importChartData?.totalWeight)}
              </Typography>
            </div>
            <Bar
              height={100}
              options={options}
              data={{
                labels: importChartData.labels,
                datasets: [
                  {
                    label: "Weight (Ton)",
                    data: importChartData.weights?.map((data) => data / 1000),
                    backgroundColor: "rgba(53, 162, 235, 0.5)",
                  },
                  {
                    label: consignmentTypeName === "Import" ? "Container Count" : "Vehicle Count",
                    data: importChartData.counts,
                    backgroundColor: "rgba(75, 192, 192, 0.5)",
                  },
                ],
              }}
            />
          </div>
        </Grid>
      </Grid>
      <CommonDialog
        isOpen={isOpendailog}
        handleClose={toggleDialogCloseHandler}
        maxWidth="lg"
        heading={
          <Box display="flex" alignItems="center">
            <Typography className={classes.heading}>{cargoGridData[0]?.cargoTypeName}</Typography>
          </Box>
        }
      >
        <CommonMaterialGrid
          rows={cargoGridData}
          columns={displayColumns}
          loader={loader}
          defaultPerPageRecord={15}
          height={550}
          deletedRow={true}
          isExport={isExport}
          exportHandler={exportHandler}
          filename={`List_of_vehicle_${Date.now()}`}
        />
      </CommonDialog>
    </>
  );
}
